import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, Sunrise, Wheat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useModals } from "@/components/ModalContext";
import { IMAGENES } from "@/constants/site";
import { Reveal } from "@/components/Reveal";

const LINEAS = [
  { texto: "El sabor de lo", acento: false },
  { texto: "artesanal,", acento: true },
  { texto: "hecho cada día.", acento: false },
];

const FACTS = [
  { icon: Wheat, label: "Procesos tradicionales" },
  { icon: Sparkles, label: "Ingredientes seleccionados" },
  { icon: Sunrise, label: "Elaboración diaria" },
];

export const Hero = () => {
  const { openContact, openSchedule } = useModals();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <section id="inicio" data-testid="hero-section" className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-[-12%] h-[560px] w-[560px] rounded-full bg-arena/50 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-16 pt-28 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-24 lg:pt-36">
        <div className="lg:col-span-6">
          <Reveal delay={0.05} y={16}>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-cafe">
              <span aria-hidden className="h-px w-9 bg-cafe/60" />
              Pastelería · Panadería — 100 % artesanal
            </p>
          </Reveal>

          <h1
            data-testid="hero-title"
            className="mt-7 font-serif text-[2.7rem] font-semibold leading-[1.06] tracking-tight text-tinta sm:text-6xl lg:text-[4.5rem]"
          >
            {LINEAS.map((linea, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${linea.acento ? "font-garamond font-medium italic text-cafe" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {linea.texto}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-md text-base leading-relaxed text-tinta/70 sm:text-lg"
            data-testid="hero-subtext"
          >
            Elaboramos diariamente panes, bollería, pastelería y productos salados
            siguiendo procesos artesanales y cuidando cada detalle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <Button
              data-testid="hero-contact-button"
              onClick={() => openContact()}
              className="rounded-full px-8 py-6 text-base"
            >
              Contactar
            </Button>
            <Button
              variant="outline"
              data-testid="hero-schedule-button"
              onClick={openSchedule}
              className="rounded-full px-8 py-6 text-base"
            >
              Agendar llamada
            </Button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-facts"
          >
            {FACTS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-sm text-tinta/70">
                <Icon className="h-4 w-4 text-cafe" strokeWidth={1.8} />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[520px]"
            data-testid="hero-image"
          >
            <div
              aria-hidden
              className="absolute -left-5 -top-5 h-full w-full rounded-b-[2.5rem] rounded-t-[999px] bg-arena/70 sm:-left-7 sm:-top-7"
            />
            <motion.div
              style={{ y }}
              className="relative h-[440px] overflow-hidden rounded-b-[2.5rem] rounded-t-[999px] sm:h-[540px] lg:h-[600px]"
            >
              <img
                src={IMAGENES.hero}
                alt="Croissants artesanales recién horneados de Pastelería Panadería Rawan"
                className="h-full w-full scale-110 object-cover"
                fetchPriority="high"
                data-zoomable
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-chocolate/25 via-transparent to-transparent"
              />
            </motion.div>
            <div className="absolute bottom-6 left-2 flex items-center gap-3 rounded-2xl bg-crema/95 px-5 py-4 shadow-lg backdrop-blur sm:-left-8 sm:bottom-10">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-chocolate">
                <Wheat className="h-5 w-5 text-crema" strokeWidth={1.8} />
              </span>
              <span className="leading-tight">
                <span className="block font-serif text-base text-tinta">Horneado cada día</span>
                <span className="block text-xs text-tinta/60">artesanía fresca, siempre</span>
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
